import { LngLat, LngLatBounds } from 'maplibre-gl'
import { StateCreator } from 'zustand'
import { DEFAULT_FIT_BOUNDS_OPTIONS } from '../home/components/map/constants'
import {
    LngLatLabelledAppUser,
    LngLatLabelledItineraryComponents,
} from '../home/components/map/interfaces/locationLabelledObject'
import { mapRefSignal } from '../home/components/map/map'
import { EphemeralStore } from './useEphemeralStore'

interface MapState {
    mapIsFullscreen: boolean
    mapDrawerIsPresented: boolean
    selectedLngLatLabelledItineraryComponents: LngLatLabelledItineraryComponents | null
    selectedLngLatLabelledTeamMember: LngLatLabelledAppUser | null
    mapViewState: MapViewState
    includeLiveLocationsInBounds: boolean
}

interface MapViewState {
    longitude: number
    latitude: number
    zoom: number
}

const defaultMapState: MapState = {
    mapIsFullscreen: false,
    mapDrawerIsPresented: false,
    selectedLngLatLabelledItineraryComponents: null,
    selectedLngLatLabelledTeamMember: null,
    mapViewState: {
        longitude: 51,
        latitude: -1,
        zoom: 2,
    },
    includeLiveLocationsInBounds: true,
}

interface MapActions {
    closeMapDrawer: () => void
    selectLngLatLabelledItineraryComponents: (
        lngLatLabelledItineraryComponents: LngLatLabelledItineraryComponents,
        center: LngLat
    ) => void
    selectLngLatLabelledTeamMember: (
        teamMember: LngLatLabelledAppUser,
        center: LngLat
    ) => void
    setMapViewState: (viewState: MapViewState) => void
    mapFlyTo: (
        center: LngLat,
        mapDrawerIsPresented: boolean,
        bounds?: LngLatBounds,
        zoom?: number
    ) => void
}

export interface MapSlice extends MapState, MapActions {}

export const createMapSlice: StateCreator<EphemeralStore, [], [], MapSlice> = (
    set,
    get
) => ({
    ...defaultMapState,
    closeMapDrawer: () => {
        set({
            mapDrawerIsPresented: false,
            selectedLngLatLabelledItineraryComponents: null,
            selectedLngLatLabelledTeamMember: null,
        })

        if (mapRefSignal.value) {
            get().mapFlyTo(mapRefSignal.value.getCenter(), false)
        }
    },
    selectLngLatLabelledItineraryComponents: (
        lngLatLabelledItineraryComponents: LngLatLabelledItineraryComponents,
        center
    ) => {
        set({
            mapDrawerIsPresented: true,
            selectedLngLatLabelledItineraryComponents:
                lngLatLabelledItineraryComponents,
            selectedLngLatLabelledTeamMember: null,
        })
        get().mapFlyTo(center, true)
    },
    selectLngLatLabelledTeamMember: (teamMember, center) => {
        set({
            mapDrawerIsPresented: true,
            selectedLngLatLabelledItineraryComponents: null,
            selectedLngLatLabelledTeamMember: teamMember,
        })
        get().mapFlyTo(center, true)
    },
    setMapViewState: (viewState: MapViewState) => {
        set({
            mapViewState: viewState,
        })
    },
    mapFlyTo: (center, mapDrawerIsPresented, bounds, zoom) => {
        if (mapRefSignal.value) {
            if (
                mapRefSignal.value.getPadding().bottom !== 0 &&
                !mapDrawerIsPresented
            ) {
                /// To reset padding because of the issues with padding
                mapRefSignal.value.setPadding({
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                })
            }

            /// Some duplicate code here as zoom and padding cant be set as single values or given as undefined
            if (bounds) {
                if (zoom) {
                    mapRefSignal.value.fitBounds(bounds, {
                        ...DEFAULT_FIT_BOUNDS_OPTIONS,
                        duration: 1000,
                        center: center,
                        zoom: zoom,
                    })
                } else {
                    mapRefSignal.value.fitBounds(bounds, {
                        ...DEFAULT_FIT_BOUNDS_OPTIONS,
                        duration: 1000,
                        center: center,
                    })
                }
            } else {
                if (mapDrawerIsPresented) {
                    mapRefSignal.value.flyTo({
                        duration: 1000,
                        center: center,
                        zoom: zoom,
                        padding: {
                            bottom: window.innerHeight * 0.5 - 70,
                        },
                    })
                } else {
                    mapRefSignal.value.flyTo({
                        ...DEFAULT_FIT_BOUNDS_OPTIONS,
                        duration: 1000,
                        center: center,
                        zoom: zoom,
                    })
                }
            }
        }
    },
})
